import clsx from 'clsx';
import { type ComponentPropsWithRef, type ReactNode, createElement } from 'react';

import { getColorStyle, getResponsiveClasses, isInlineColorValue } from '@commons/helpers/styles';
import type {
  ColorOrInline,
  FontWeight,
  ResponsiveStyle,
  Spacing,
  TextAlignment,
  TextTransform,
} from '@commons/types/styles';

import styles from './index.module.scss';

/**
 * Supported title nodes.
 */

export type TitleNode = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Supported text nodes.
 */

export type TextNode = 'p' | 'span' | TitleNode;

/**
 * Supported text levels (maps to Drupal CKEditor styles).
 */

export type TextLevel =
  | 'display-1'
  | 'display-2'
  | 'label-lg'
  | 'label-md'
  | 'label-sm'
  | 'body-lg'
  | 'body-md'
  | 'body-sm'
  | 'body-xs'
  | TextNode;

/**
 * Text component props.
 */

export type TextProps = ComponentPropsWithRef<'p'> & {
  /**
   * The HTML element to render.
   *
   * @example <Text as="h1">Hello World</Text>
   */
  as?: TextNode;

  /**
   * The visual level of the text.
   *
   * Corresponds to an HTML text node or a predefined level.
   * A responsive object can be used to set different levels for different breakpoints.
   *
   * @example
   * <Text as="h1" level="h2">Hello World</Text>
   * <Text level={{ xs: 'body-lg', lg: 'h6' }}>Hello World</Text>
   */
  level?: ResponsiveStyle<TextLevel>;

  /**
   * The color of the text.
   *
   * Can be a predefined or inline color value.
   * `primary` is a special value that uses the primary color.
   *
   * @example
   * <Text color="red-1">Hello World</Text>
   * <Text color="primary">Hello World</Text>
   * <Text color="#ff0000">Hello World</Text>
   */
  color?: ColorOrInline | 'primary';

  /**
   * The text alignment.
   *
   * @example <Text align="center">Hello World</Text>
   */
  align?: TextAlignment;

  /**
   * The font weight.
   *
   * @example <Text weight="bold">Hello World</Text>
   */
  weight?: FontWeight;

  /**
   * The text content.
   */
  children: ReactNode;

  /**
   * Additional class names.
   */
  className?: string;

  /**
   * Whether to underline the text.
   *
   * @example <Text underline>Hello World</Text>
   */
  underline?: boolean;

  /**
   * The text transform.
   *
   * @example <Text transform="uppercase">Hello World</Text>
   */
  transform?: TextTransform;

  /**
   * The margin top.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <Text marginTop="md">Hello World</Text>
   * <Text marginTop={20}>Hello World</Text>
   */
  marginTop?: Spacing | number;

  /**
   * The margin bottom.
   *
   * Can be a spacing value or a number (applied as an inline style).
   *
   * @example
   * <Text marginBottom="md">Hello World</Text>
   * <Text marginBottom={20}>Hello World</Text>
   */
  marginBottom?: Spacing | number;
};

/**
 * Text component.
 *
 * @see https://www.figma.com/design/XxDeNES3KCxZ3qRZQpPGV5/grd025-crisis24-website-02?node-id=5661-4169&node-type=frame&m=dev
 */

export const Text = (props: TextProps) => {
  const {
    as = 'p',
    level,
    color,
    align,
    weight,
    children,
    className,
    underline,
    transform,
    marginTop: mT,
    marginBottom: mB,
    style,
    ref,
    ...rest
  } = props;

  const responsiveClasses = [...getResponsiveClasses('level', level)];

  const classes = [
    'text',
    styles.text,
    level && level,
    responsiveClasses.map((className) => styles[className]),
    color && !isInlineColorValue(color) && `color-${color}`,
    align && `text-align-${align}`,
    weight && styles[`fontWeight-${weight}`],
    underline && styles.underline,
    transform && styles[`transform-${transform}`],
    mT && typeof mT === 'string' && `marginTop-${mT}`,
    mB && typeof mB === 'string' && `withSpacingMarginBottom marginBottom-${mB}`,
    className,
  ];

  const inlineStyles = {
    marginTop: mT && typeof mT === 'number' ? `${mT}px` : undefined,
    marginBottom: mB && typeof mB === 'number' ? `${mB}px` : undefined,
    color: getColorStyle(color),
    ...style,
  };

  return createElement(
    as,
    { ref, className: clsx(classes), style: inlineStyles, ...rest },
    children
  );
};

export default Text;
